import { useCallback, useMemo } from 'react';

import { usePermissions } from '../../hooks/api';
import type { Action, Permission, ResourceType } from '../../../typings/permissions';

type BasePermissionsKey = `${ResourceType}|${Action}`;
type PermissionsKey =
  | BasePermissionsKey
  | `${BasePermissionsKey}|${string}`
  | `${BasePermissionsKey}|${string}|${string}`;

/**
 * Takes a permission and constructs a lookup key based on the permissions object.
 *
 * @param {Permission} permission - A given permission.
 */
const getPermissionKey = (permission: Permission): PermissionsKey => {
  let key = `${permission.resourceType}|${permission.action}`;
  if (permission.projectId) {
    key += `|${permission.projectId}`;
  }
  if (permission.releaseGroupId) {
    key += `|${permission.releaseGroupId}`;
  }
  if (permission.teamId) {
    key += `|${permission.teamId}`;
  }
  return key as PermissionsKey;
};

/**
 * Hook which will auto-fetch the user permissions (once per minute), and then
 * store the results in a Set so that we can quickly look them up. This hook
 * returns a `hasPermission` function which can be called with a given permission
 * to check if the user has that permission.
 *
 * @example // In a component
 * const MyComponent = () => {
 *   const hasPermission = useHasPermission();
 *   const canViewSettings = hasPermission({ action: Action.View, resourceType: ResourceType.Settings });
 *   if (!canViewSettings) {
 *     return null;
 *   }
 *   return <Settings />;
 * };
 *
 * @example // In a custom hook
 * const useCanViewSettings = () => {
 *   const hasPermission = useHasPermission();
 *   return hasPermission({ action: Action.View, resourceType: ResourceType.Settings });
 * };
 *
 * The `hasPermission` function accepts an optional `forAnyScope` parameter. When true, it will check if the user has
 * the given permission for any project, release group, or team. This can be helpful if the user only has permission to
 * create a project within a team and you only care whether the user can create a project in general, the eventual team
 * does not yet matter.
 *
 * @example // In a component
 * const MyComponent = () => {
 *   const hasPermission = useHasPermission();
 *   const canCreateProject = hasPermission({ action: Action.Create, resourceType: ResourceType.Project }, true);
 *   if (!canCreateProject) {
 *     return null;
 *   }
 *   return <CreateProject />;
 * };
 */
const useHasPermission = () => {
  const { data: permissions } = usePermissions();

  //  Store the permissions in a Set for quick lookups.
  //  Note that this will be improved/merged with our existing PermissionsCacheManager.serializePermissions
  const permissionsSet = useMemo<Set<PermissionsKey>>(() => {
    if (!permissions) {
      return new Set();
    }
    return new Set(permissions.map((permission) => getPermissionKey(permission)));
  }, [permissions]);

  //  Function to actually look up the user permissions.
  const hasPermission = useCallback((permission: Permission, forAnyScope: boolean = false): boolean => {
    const hasExactPermission = permissionsSet.has(getPermissionKey(permission));
    if (hasExactPermission) {
      return true;
    }

    if (forAnyScope) {
      return permissionsSet.values().some((key) => key.startsWith(`${permission.resourceType}|${permission.action}|`));
    }

    return false;
  }, [permissionsSet]);

  return hasPermission;
};

export default useHasPermission;
